import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { addDays, format } from 'date-fns';
import {
  DxButtonModule,
  DxContextMenuModule,
  DxDataGridModule,
  DxListModule,
  DxSchedulerModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxToolbarModule,
  DxTooltipModule,
} from 'devextreme-angular';
import { ItemClickEvent } from 'devextreme/ui/context_menu';
import { DragStartEvent } from 'devextreme/ui/draggable';
import notify from 'devextreme/ui/notify';
import {
  AppointmentContextMenuEvent,
  AppointmentDblClickEvent,
  AppointmentFormOpeningEvent,
  AppointmentRenderedEvent,
  AppointmentUpdatingEvent,
  CellContextMenuEvent,
  OptionChangedEvent,
} from 'devextreme/ui/scheduler';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { LoaderComponent } from '../../loader/loader.component';
import { ContextService } from '../../services/context.service';
import { PlanningHelperService } from '../../services/planning-helper.service';
import { VehicleFilterCacheService } from '../../services/vehicle-cache.service';
import { VehicleScheduleRefreshService } from '../../services/vehicle-schedule-refresh.service';
import {
  DriverDto,
  DriverState,
  FleetService,
  ProjectService,
  SchedulerProjectInfoDto,
  SchedulerProjectLoadunitDto,
  UpdateProjectError,
  VehicleDto,
} from '../../shared/generated/backend';
import { AssignmentType } from '../../shared/generated/backend/model/assignmentType';
import { CreateProjectData } from '../../shared/generated/backend/model/createProjectData';
import { ProcessingState } from '../../shared/generated/backend/model/processingState';
import { UpdateProjectData } from '../../shared/generated/backend/model/updateProjectData';
import { SessionService } from '../../shared/services/session.service';
import { SharedModule } from '../../shared/shared.module';
import { createBusyState } from '../../shared/util/busy-state';
import {
  Appointment,
  AppointmentType,
  BaseSchedulerComponent,
  SchedulerViews,
  ToolbarItem,
} from '../base-scheduler-component';
import { NotificationState } from './../../shared/generated/backend/model/notificationState';
import {
  BadgeVariantComponent,
  Shape,
} from '../../shared/components/badge-variant/badge-variant/badge-variant.component';

interface Vehicle extends VehicleDto {
  id: number;
  text: string;
  driver?: DriverDto;
  volume?: string;
}

interface FormattedLoadunit {
  city: string;
  isPickup: boolean;
  dispFrom?: string;
}

interface AppointmentVm extends Appointment {
  formattedLoadunits: FormattedLoadunit[];
  appointmentColor: string;
  loadunitsContainerColor: string | null;
  isWeekAppointment: boolean;
  isAdminVisible: boolean;
  appointmentCssClass: string | string[] | Record<string, boolean>;
  isPrimeLogistic: boolean;
}

interface SchedulerCell {
  startDate: Date;
  endDate: Date;
  groups: undefined;
}

interface SchedulerStyleMap {
  '--row-height': string;
  '--appointment-height'?: string;
}

export const PROCESSING_STATE_COLORS: { [key in ProcessingState]: string } = {
  TransportationScheduling: '#FFA07A',
  TransportationSchedulingDone: '#98FB98',
  Approved: '#41E342',
  ProcessingByWarehouse: '#FFFF00',
  FixedByWarehouse: '#FFA500',
  Rolling: '#dbdbdb',
  Completed: '#A9A9A9',
  WaitingToUnload: '#b7e0f0ff',
  Unloading: '#81cdebff',
};

export const COLORS = {
  SOFT_VIOLET: '#8E7CC3',
  SOFT_RED: '#E57373',
};

interface SortItem {
  text: string;
  value: string;
}

interface ContextMenuItem {
  id: string;
  text: string;
}

type SchedulerView = SchedulerViews[number];

@Component({
  selector: 'app-vehicle-schedule',
  imports: [
    DxTooltipModule,
    DxSchedulerModule,
    DxTagBoxModule,
    DxButtonModule,
    FormsModule,
    DxDataGridModule,
    CommonModule,
    DxToolbarModule,
    DxListModule,
    DxSelectBoxModule,
    LoaderComponent,
    BadgeVariantComponent,
    SharedModule,
    DxContextMenuModule,
    DxScrollViewModule,
  ],
  templateUrl: './vehicle-schedule.component.html',
  styleUrls: ['./vehicle-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleScheduleComponent extends BaseSchedulerComponent implements OnInit, OnDestroy {
  private readonly volumeResource = {
    fieldExpr: 'volume',
    allowMultiple: false,
    dataSource: [
      { id: '40t', text: '40t' },
      { id: '7.5t', text: '7.5t' },
    ],
    valueExpr: 'id',
    displayExpr: 'text',
  };

  private readonly vehicleResource = {
    fieldExpr: 'VehicleId',
    allowMultiple: false,
    dataSource: [] as Vehicle[],
    valueExpr: 'id',
    displayExpr: 'text',
  };

  resources = [this.volumeResource, this.vehicleResource];

  sortOptions: SortItem[] = [];
  contextMenuItems: ContextMenuItem[] = [];

  private contextMenuData: CellContextMenuEvent | AppointmentContextMenuEvent | null = null;
  contextMenuVisible = false;
  // Guards against starting a new drag/resize update while the previous one is still being saved.
  readonly dragUpdate = createBusyState();

  selectedCells: { startDate: Date; endDate: Date; groups: { VehicleId: number } }[] = [];

  allVehicles: Vehicle[] = [];
  administrators: string[] = [];
  selectedAdministrators: string[] = [];
  selectedVehicleIds: number[] = [];

  showCompactView = false;
  currentSortOption = '';
  processingStateColors = PROCESSING_STATE_COLORS;

  viewCompactStyles: SchedulerStyleMap = {
    '--row-height': '60px',
  };

  lastClickTime = 0;
  lastClickCell: SchedulerCell | null = null;
  clickTimeout: ReturnType<typeof setTimeout> | null = null;
  currentSelectedAppointment: Appointment | undefined;

  refreshSubscription?: Subscription;
  selectedVehicleIdsSubscription?: Subscription;

  AppointmentType = AppointmentType;
  readonly Shape = Shape;

  readonly selectedVehicleIds$ = new BehaviorSubject<number[]>([]);
  private isInitialising = true;

  private toolbarPrepared = false;
  private repaintQueued = false;

  private readonly timelinessCache = new Map<string, boolean>();
  private readonly appointmentCheckCache = new Map<string, boolean>();

  schedulerReady = false;
  private hasDoneInitialSchedulerRecreate = false;

  private contextService = inject(ContextService);
  private fleetService = inject(FleetService);
  private projectService = inject(ProjectService);
  private scheduleRefreshService = inject(VehicleScheduleRefreshService);
  private translationService = inject(TranslateService);
  private cache = inject(VehicleFilterCacheService);
  private planningHelperService = inject(PlanningHelperService);
  private sessionService = inject(SessionService);

  constructor() {
    super();
  }

  override async ngOnInit(): Promise<void> {
    this.schedulerReady = false;

    await super.ngOnInit();
    this.updateViewCompactStyles();

    this.selectedVehicleIdsSubscription = this.selectedVehicleIds$.subscribe(ids => {
      this.selectedVehicleIds = ids;
      this.updateResourcesDataSource();
      this.syncToolbarState();
      this.cdr.markForCheck();
    });

    this.refreshSubscription = this.scheduleRefreshService.refresh$.subscribe(() => {
      this.loadAppointments();
    });

    await new Promise(resolve => setTimeout(resolve, 0));
    this.schedulerReady = true;
    this.cdr.markForCheck();
  }

  override ngOnDestroy(): void {
    this.selectedVehicleIdsSubscription?.unsubscribe();
    this.refreshSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  onOptionChanged(e: OptionChangedEvent): void {
    if (e.name === 'selectedCellData' && e.value && Array.isArray(e.value) && e.value.length > 0) {
      this.selectedCells = [...e.value];
      this.cdr.markForCheck();
    }
  }

  protected override async loadInitialData(): Promise<void> {
    this.sortOptions = [
      { text: this.translationService.instant('SCHEDULE.SORTING.VEHICLE'), value: 'vehicleId' },
      { text: this.translationService.instant('SCHEDULE.SORTING.PLATE_NUMBER'), value: 'identifier' },
    ];

    this.contextMenuItems = [
      { text: this.translationService.instant('PROJECT_STEP.NEWPROJECT'), id: 'create-project' },
    ];

    this.cdr.markForCheck();
    await this.loadAppointments();
  }

  async loadVehicles(): Promise<void> {
    try {
      const [vehicles, drivers] = await Promise.all([
        firstValueFrom(this.fleetService.getAllVehicles()),
        firstValueFrom(this.fleetService.getAllDrivers()),
      ]);

      this.drivers = drivers.map(d => ({
        ...d,
        id: d.employeeNumber,
        text: '',
      }));

      let toggle = true;
      this.allVehicles = vehicles.map(vehicle => {
        const driver = drivers.find(d => d.employeeNumber === vehicle.employeeNumber);
        const volume = toggle ? '40t' : '7.5t';
        toggle = !toggle;

        return {
          ...vehicle,
          id: vehicle.vehicleId,
          text: vehicle.displayName,
          driver,
          volume,
        };
      });

      const haveCache = this.cache.has();
      const cachedIds = this.cache.get();

      if (haveCache) {
        this.selectedVehicleIds = cachedIds.filter(id => this.allVehicles.some(v => v.id === id));
      }

      if (!haveCache && this.selectedVehicleIds.length === 0) {
        this.selectedVehicleIds = this.allVehicles.map(v => v.id);
      }

      this.sortVehicles(this.currentSortOption, false);
      this.selectedVehicleIds$.next([...this.selectedVehicleIds]);
      this.cache.set(this.selectedVehicleIds);
      this.isInitialising = false;

      this.syncToolbarState();
      this.cdr.markForCheck();
    } catch (err: unknown) {
      this.notifyError(err);
      console.error(err);
    }
  }

  override async loadAppointments(): Promise<void> {
    try {
      this.isLoading = true;
      this.cdr.markForCheck();

      const scrollPosition = this.getScrollPosition('.driver-name');

      this.appointments = [];
      await this.loadVehicles();

      const { fromDate, toDate } = this.getUpdatedDateRange();
      // Add one day to the toDate to include the whole day)
      const projects = await firstValueFrom(
        this.projectService.getSchedulerProjects(
          format(fromDate, 'yyyy-MM-dd'),
          format(addDays(toDate, 1), 'yyyy-MM-dd')
        )
      );

      const appointments: Appointment[] = projects.map((project: SchedulerProjectInfoDto) => {
        return {
          ...project,
          Text: project.TrapoText || 'Default Text',
          startDate: project.FromDateTime ? new Date(project.FromDateTime) : undefined,
          endDate: project.UntilDateTime ? new Date(project.UntilDateTime) : undefined,
          ProcessingState: project.ProcessingState,
        } as Appointment;
      });

      const enrichedAppointments = await Promise.all(
        appointments.map(async (appointment: Appointment) => {
          const checkNeeded = await this.computeCheckNeeded(appointment);

          return {
            ...appointment,
            Text: appointment.TrapoText || 'Default Text',
            NoteType: null,
            AppointmentType: AppointmentType.PROJECT,
            checkNeeded,
          } as Appointment;
        })
      );

      this.appointments = enrichedAppointments.map(appointment => this.toAppointmentVm(appointment));

      this.updateAdministrators();
      this.loadWeekAppointments();

      if (!this.hasDoneInitialSchedulerRecreate) {
        this.hasDoneInitialSchedulerRecreate = true;
        await this.recreateSchedulerAfterInitialLoad();
      }

      this.refreshSchedulerViewState();
      this.requestSchedulerRepaint();

      // one extra delayed repaint after toolbar/view/date-range state has settled
      setTimeout(() => {
        this.scheduler?.instance.repaint();
        this.cdr.markForCheck();
      }, 50);

      setTimeout(() => {
        this.isLoading = false;

        this.refreshSchedulerViewState();
        this.scheduler?.instance.repaint();

        const id = scrollPosition.verticalElement ? parseInt(scrollPosition.verticalElement.id, 10) : undefined;
        const date = this.scrollPositionToDate(scrollPosition);
        this.scheduler?.instance.scrollTo(date, { VehicleId: id });

        this.cdr.markForCheck();
      }, 100);

      this.syncToolbarState();
      this.cdr.markForCheck();
    } catch (err: unknown) {
      this.notifyError(err);
      console.error(err);

      this.appointments = [];
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  protected override prepareToolbar(): void {
    super.prepareToolbar();

    const toolbarInstance = this.getToolbarInstance();
    if (!toolbarInstance) {
      return;
    }

    const toolbarOrder = [
      'dateRange',
      'vehicleFilter',
      'sortVehicles',
      'administratorFilter',
      'compactView',
      'viewSwitcher',
      'refreshButton',
      'autoPolling',
    ];

    const items = [...(toolbarInstance.option('items') as ToolbarItem[])];

    this.upsertToolbarItem(items, {
      widget: 'dxTagBox',
      name: 'vehicleFilter',
      location: 'before',
      locationInMenu: 'always',
      options: {
        dataSource: this.allVehicles,
        displayExpr: 'text',
        valueExpr: 'id',
        value: this.selectedVehicleIds,
        onValueChanged: (e: { value: number[] }) => this.onVehicleSelectionChanged(e),
        placeholder: this.translationService.instant('SCHEDULE.SELECT_VEHICLES'),
        showClearButton: true,
        searchEnabled: true,
        width: 200,
        multiline: false,
        maxDisplayedTags: 2,
        showSelectionControls: true,
        applyValueMode: 'useButtons',
        stylingMode: 'outlined',
        maxFilterQueryLength: 5000,
        onOpened: () => this.toolboxWidgetOpened(),
        onClosed: () => this.toolboxWidgetClosed(),
      },
    });

    this.upsertToolbarItem(items, {
      widget: 'dxSelectBox',
      name: 'sortVehicles',
      location: 'before',
      locationInMenu: 'always',
      options: {
        items: this.sortOptions,
        displayExpr: 'text',
        valueExpr: 'value',
        value: this.currentSortOption,
        placeholder: this.translationService.instant('SCHEDULE.SORTING.SORT_BY'),
        onValueChanged: (e: { value: string }) => this.sortVehicles(e.value),
        width: 150,
        onOpened: () => this.toolboxWidgetOpened(),
        onClosed: () => this.toolboxWidgetClosed(),
      },
    });

    this.upsertToolbarItem(items, {
      widget: 'dxTagBox',
      name: 'administratorFilter',
      location: 'before',
      locationInMenu: 'always',
      options: {
        dataSource: this.administrators,
        displayExpr: 'this',
        valueExpr: 'this',
        value: this.selectedAdministrators,
        onValueChanged: (e: { value: string[] }) => {
          this.selectedAdministrators = e.value;
          this.updateAdminVisibilityOnly();
          this.requestSchedulerRepaint();
          this.cdr.markForCheck();
        },
        placeholder: this.translationService.instant('SCHEDULE.SORTING.BY_ADMIN'),
        showClearButton: true,
        searchEnabled: true,
        width: 200,
        multiline: false,
        maxDisplayedTags: 2,
        showSelectionControls: true,
        applyValueMode: 'useButtons',
        stylingMode: 'outlined',
        onOpened: () => this.toolboxWidgetOpened(),
        onClosed: () => this.toolboxWidgetClosed(),
      },
    });

    this.upsertToolbarItem(items, {
      widget: 'dxCheckBox',
      name: 'compactView',
      location: 'before',
      locationInMenu: 'always',
      options: {
        text: this.translationService.instant('SCHEDULE.SHOW_COMPACT_VIEW'),
        value: this.showCompactView,
        onValueChanged: (e: { value: boolean }) => this.setCompactView(e.value),
      },
    });

    items.sort((a, b) => {
      const ai = a.name ? toolbarOrder.indexOf(a.name) : Number.MAX_SAFE_INTEGER;
      const bi = b.name ? toolbarOrder.indexOf(b.name) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });

    toolbarInstance.option('items', items);
  }

  private syncToolbarState(): void {
    this.syncToolbarItemOptions('vehicleFilter', {
      dataSource: this.allVehicles,
      value: this.selectedVehicleIds,
    });

    this.syncToolbarItemOptions('sortVehicles', {
      items: this.sortOptions,
      value: this.currentSortOption,
    });

    this.syncToolbarItemOptions('administratorFilter', {
      dataSource: this.administrators,
      value: this.selectedAdministrators,
    });

    this.syncToolbarItemOptions('compactView', {
      value: this.showCompactView,
    });
  }

  loadWeekAppointments(): void {
    this.allVehicles
      .filter(v => v.driver?.weekStart || v.driver?.weekStartShort)
      .forEach(v => {
        const weekStart = v.driver?.weekStart;
        const weekStartShort = v.driver?.weekStartShort;

        if (weekStart) {
          const appointment = this.createWeekAppointment(v, new Date(weekStart), AppointmentType.LONG_WEEK);
          this.appointments.push(this.toAppointmentVm(appointment));
        }

        if (weekStartShort) {
          const appointment = this.createWeekAppointment(v, new Date(weekStartShort), AppointmentType.SHORT_WEEK);
          this.appointments.push(this.toAppointmentVm(appointment));
        }
      });

    this.updateAdminVisibilityOnly();
  }

  onCellContextMenu(e: CellContextMenuEvent | AppointmentContextMenuEvent): void {
    this.contextMenuData = e;
    e.event?.preventDefault();

    if ('appointmentData' in e && e.appointmentData) {
      e.event?.stopPropagation();
      this.contextMenuVisible = false;
      this.cdr.markForCheck();
      return;
    }

    this.contextMenuVisible = true;
    this.cdr.markForCheck();
  }

  onContextMenuItemClick(e: ItemClickEvent): void {
    e.event?.preventDefault();

    if (!this.selectedCells || this.selectedCells.length === 0) {
      notify(this.translationService.instant('SCHEDULE.INVALID_CELL_SELECTION'), 'error', 3000);
      return;
    }

    const overallStartDate = this.selectedCells.reduce(
      (min: Date, cell: { startDate: Date; endDate: Date }) => (cell.startDate < min ? cell.startDate : min),
      this.selectedCells[0].startDate
    );

    const overallEndDate = this.selectedCells.reduce(
      (max: Date, cell: { startDate: Date; endDate: Date }) => (cell.endDate > max ? cell.endDate : max),
      this.selectedCells[0].endDate
    );

    const vehicleId = this.selectedCells[0].groups?.VehicleId;
    if (!overallStartDate || !overallEndDate || !vehicleId) {
      notify(this.translationService.instant('SCHEDULE.INVALID_CELL_SELECTION'), 'error', 3000);
      return;
    }

    if (this.isAppointmentOverlapping(overallStartDate, overallEndDate, vehicleId)) {
      notify(this.translationService.instant('SCHEDULE.CANNOT_OVERLAP_VEHICLE_APPOINTMENTS'), 'error', 3000);
      return;
    }

    const projectData: CreateProjectData = {
      fromDate: format(overallStartDate, 'yyyy-MM-dd HH:mm:ss'),
      toDate: format(overallEndDate, 'yyyy-MM-dd HH:mm:ss'),
      vehicleId,
    };

    this.createNewProject(projectData);

    this.selectedCells = [];
    this.contextMenuVisible = false;
    this.cdr.markForCheck();
  }

  private createWeekAppointment(vehicle: Vehicle, startDate: Date, appointmentType: AppointmentType): Appointment {
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 6);

    return {
      ...vehicle,
      Text: this.translationService.instant(
        'SCHEDULE.' + (appointmentType === AppointmentType.LONG_WEEK ? 'LONG_WEEK' : 'SHORT_WEEK')
      ),
      startDate,
      endDate,
      NoteType: null,
      AppointmentType: appointmentType,
      VehicleId: vehicle.id,
      Id: 0,
      ProjectNo: 0,
      ProcessingState: ProcessingState.ProcessingByWarehouse,
      FromDateTime: format(startDate, 'yyyy-MM-dd HH:mm:ss'),
      UntilDateTime: format(endDate, 'yyyy-MM-dd HH:mm:ss'),
      PickupWeight: 0,
      PickupVolume: 0,
      DeliveryWeight: 0,
      DeliveryVolume: 0,
      DriverState: DriverState.NotDispatched,
      NotificationSend: NotificationState.Nothing,
      Priority: 1,
      disabled: true,
      Loadunits: [],
    };
  }

  onVehicleSelectionChanged(e: { value: number[] }): void {
    if (this.isInitialising) {
      return;
    }

    this.selectedVehicleIds = e.value;
    this.cache.set(this.selectedVehicleIds);
    this.updateResourcesDataSource();
    this.syncToolbarState();
    this.cdr.markForCheck();
  }

  updateResourcesDataSource(): void {
    const vehicles = this.allVehicles.filter(v => this.selectedVehicleIds.includes(v.id));

    if (this.sameVehicleIds(this.vehicleResource.dataSource, vehicles)) {
      return;
    }

    this.vehicleResource.dataSource = vehicles;

    if (this.scheduler?.instance) {
      this.scheduler.instance.option('resources', this.resources);
    }

    this.requestSchedulerRepaint();
  }

  sortVehicles(sortOption: string, repaint = true): void {
    this.currentSortOption = sortOption;

    switch (sortOption) {
      case 'vehicleId':
        this.allVehicles = [...this.allVehicles].sort((a, b) => a.id - b.id);
        break;
      case 'identifier':
        this.allVehicles = [...this.allVehicles].sort((a, b) => (a.identifier || '').localeCompare(b.identifier || ''));
        break;
      default:
        this.allVehicles = [...this.allVehicles];
        break;
    }

    this.updateResourcesDataSource();
    this.syncToolbarState();

    if (repaint) {
      this.requestSchedulerRepaint();
    }

    this.cdr.markForCheck();
  }

  onAppointmentUpdating = (e: AppointmentUpdatingEvent) => {
    // a previous drag is still being saved - reject overlapping updates instead of racing them
    if (this.dragUpdate.busy()) {
      e.cancel = true;
      return;
    }

    if (!e.newData || !e.newData.startDate || !e.newData.endDate) {
      notify(this.translationService.instant('ERROR.INVALID'), 'error', 3000);
      console.error('Invalid drag data');
      e.cancel = true;
      return;
    }

    const draggedAppointment = e.newData as Appointment;
    const newStartDate = new Date(draggedAppointment.startDate!);
    const newEndDate = new Date(draggedAppointment.endDate!);
    const newVehicleId = draggedAppointment.VehicleId!;
    const projectNo = draggedAppointment.ProjectNo;

    const isOverlapping = this.isAppointmentOverlapping(newStartDate, newEndDate, newVehicleId, projectNo);
    if (isOverlapping) {
      e.cancel = true;
      notify(
        { message: this.translationService.instant('SCHEDULE.CANNOT_OVERLAP_VEHICLE_APPOINTMENTS'), shading: false },
        'error',
        3000
      );
      return;
    }

    if (
      e.oldData.startDate.getTime() === e.newData.startDate.getTime() &&
      e.oldData.endDate.getTime() !== e.newData.endDate.getTime()
    ) {
      if (!this.canMutateProjectEndDate(draggedAppointment)) {
        e.cancel = true;
        notify(
          this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ILLEGAL_IN_STATE'),
          'error',
          3000
        );
        return;
      }
    } else if (draggedAppointment.ProcessingState !== ProcessingState.TransportationScheduling) {
      e.cancel = true;
      notify(this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.PROJECT_PLANNED'), 'error', 3000);
      return;
    } else if (draggedAppointment.DriverState !== DriverState.NotDispatched) {
      e.cancel = true;
      notify(this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.DRIVER_INFORMED'), 'error', 3000);
      return;
    }

    try {
      const vehicleChanged = e.oldData.VehicleId !== draggedAppointment.VehicleId;
      if (vehicleChanged) {
        this.tryChangeDriver(draggedAppointment);
      }

      void this.dragUpdate.run(() => this.updateProject(draggedAppointment));
    } catch (error) {
      console.error('Error in update process:', error);
      e.cancel = true;
    }
  };

  canMutateProjectEndDate(appointment: Appointment): boolean {
    switch (appointment.ProcessingState) {
      case ProcessingState.TransportationScheduling:
      case ProcessingState.TransportationSchedulingDone:
      case ProcessingState.Approved:
      case ProcessingState.ProcessingByWarehouse:
      case ProcessingState.FixedByWarehouse:
      case ProcessingState.Rolling:
        return true;
      default:
        return false;
    }
  }

  tryChangeDriver(appointment: Appointment): void {
    const newVehicleId = appointment.VehicleId;
    if (!newVehicleId) return;

    const newVehicle = this.allVehicles.find(vehicle => vehicle.vehicleId === newVehicleId);
    if (!newVehicle) return;

    const newDriverId = newVehicle.driver?.employeeNumber;
    const newDriver = newVehicle.driver?.identifier;

    appointment.DriverId = newDriverId;
    appointment.Driver = newDriver ?? '';
  }

  async updateProject(appointment: Appointment): Promise<void> {
    const updateData: UpdateProjectData = {
      FromDateTime: format(appointment.startDate!, "yyyy-MM-dd'T'HH:mm:ss"),
      UntilDateTime: format(appointment.endDate!, "yyyy-MM-dd'T'HH:mm:ss"),
      VehicleId: appointment.VehicleId!,
      DriverId: appointment.DriverId!,
      NotificationSend: appointment.NotificationSend!,
      ProcessingState: appointment.ProcessingState,
      Priority: appointment.Priority,
      RampNeeded: appointment.RampNeeded,
      TunnelCode: appointment.TunnelCode,
      Administrator: appointment.Administrator,
      ProjectText: appointment.TrapoText,
      DriverText: appointment.DriverText,
      StockText: appointment.StockText,
    };

    try {
      await firstValueFrom(this.projectService.updateProject(appointment.ProjectNo, updateData));
      notify(this.translationService.instant('SCHEDULE.PROJECT_UPDATE_SUCCESS'), 'success', 3000);
    } catch (error) {
      console.error(error);
      const err = error as HttpErrorResponse;

      if (err.status === 409 && err.error === UpdateProjectError.IllegalForProcessingState) {
        notify(
          this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ILLEGAL_IN_STATE'),
          'error',
          3000
        );
      } else if (typeof err.error === 'string') {
        notify(this.translationService.instant('ERROR.' + err.error), 'error', 3000);
      } else if (Array.isArray(err.error)) {
        const message = this.translationService.instant('PROJECT_PLANNING.OM_CHECK') + err.error.join(', ');
        notify(message, 'warning', 6000);
      }

      await this.loadAppointments();
    }
  }

  private updateViewCompactStyles(): void {
    this.viewCompactStyles = this.showCompactView
      ? {
          '--row-height': '24px',
          '--appointment-height': '22px',
        }
      : {
          '--row-height': '60px',
        };
  }

  async setCompactView(value: boolean): Promise<void> {
    if (this.showCompactView === value) {
      return;
    }

    this.showCompactView = value;
    this.updateViewCompactStyles();
    this.syncToolbarState();

    await new Promise(resolve => setTimeout(resolve, 50));
    this.requestSchedulerRepaint();
    this.cdr.markForCheck();
  }

  updateAdministrators(): void {
    this.administrators = Array.from(
      new Set(
        this.appointments
          .map(appointment => appointment.Administrator)
          .filter((admin): admin is string => admin !== undefined && admin !== null)
      )
    );

    this.syncToolbarState();
    this.cdr.markForCheck();
  }

  onAppointmentDblClick(e: AppointmentDblClickEvent): void {
    e.cancel = true;
    e.event?.preventDefault();

    const appointmentData = e.appointmentData as Appointment;
    if (appointmentData && appointmentData.ProjectNo) {
      setTimeout(() => {
        this.contextService.openContextView(`project/${appointmentData.ProjectNo}`);
      }, 100);
    }
  }

  private isAppointmentOverlapping(
    newStartDate: Date,
    newEndDate: Date,
    newVehicleId: number,
    projectNo: number | null = null
  ): boolean {
    return this.appointments.some(appointment => {
      const appointmentStart = new Date(appointment.FromDateTime || '');
      const appointmentEnd = new Date(appointment.UntilDateTime || '');
      const appointmentVehicleId = appointment.VehicleId;
      const appointmentProjectNo = appointment.ProjectNo;

      if (
        appointment.AppointmentType === AppointmentType.LONG_WEEK ||
        appointment.AppointmentType === AppointmentType.SHORT_WEEK
      ) {
        return false;
      }

      if (projectNo !== null && projectNo === appointmentProjectNo) {
        return false;
      }

      return (
        newVehicleId === appointmentVehicleId &&
        ((newStartDate < appointmentEnd && newEndDate > appointmentStart) ||
          (appointmentStart < newEndDate && appointmentEnd > newStartDate))
      );
    });
  }

  private createNewProject(projectData: CreateProjectData): void {
    this.projectService.createProject(projectData).subscribe({
      next: () => {
        notify(this.translationService.instant('SCHEDULE.PROJECT_CREATE_SUCCESS'), 'success', 3000);
        this.loadAppointments();
      },
      error: error => {
        console.error('Error creating project:', error);
        notify(this.translationService.instant('SCHEDULE.PROJECT_CREATE_FAIL'), 'error', 3000);
      },
    });
  }

  onAppointmentFormOpening(event: AppointmentFormOpeningEvent): void {
    this.currentSelectedAppointment = event.appointmentData as Appointment;
    event.cancel = true;
    this.cdr.markForCheck();
  }

  onProjectClick(projectId: number): void {
    const windowFeatures = 'width=1200,height=800,left=100,top=100';
    window.open(`/tour(right:project/${projectId})`, '_blank', windowFeatures);
  }

  onTemplateClick(event: Event, appointmentData: Appointment): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('project-id')) {
      event.stopPropagation();
      const projectId = appointmentData.ProjectNo;
      this.onProjectClick(projectId!);
    }
  }

  getProjectColor(appointmentData: Appointment): string {
    const baseColor =
      this.processingStateColors[appointmentData.ProcessingState || ProcessingState.TransportationScheduling];
    return appointmentData.checkNeeded ? `linear-gradient(to right, ${baseColor} 90%, red 100%)` : baseColor;
  }

  getAppointmentColor(appointment: Appointment): string {
    switch (appointment.AppointmentType) {
      case AppointmentType.LONG_WEEK:
      case AppointmentType.SHORT_WEEK:
        return this.getWeekColor(appointment);
      case AppointmentType.PROJECT:
        return this.getProjectColor(appointment);
      default:
        return '#FFFFFF';
    }
  }

  getLoadunitsContainerColor(appointmentData: Appointment): string | null {
    if (appointmentData.Loadunits && appointmentData.Loadunits.some(loadunit => loadunit.ChangeState !== 0)) {
      return COLORS.SOFT_VIOLET;
    }

    return this.processingStateColors[appointmentData.ProcessingState || ProcessingState.TransportationScheduling];
  }

  formatLoadunits(loadunits: SchedulerProjectLoadunitDto[] | undefined): FormattedLoadunit[] {
    if (!loadunits || loadunits.length === 0) {
      return [];
    }

    return loadunits
      .filter(lu => lu.ActionCity)
      .map(lu => ({
        city: lu.ActionCity as string,
        isPickup:
          lu.AssignmentType === AssignmentType.PickupRentalPurchasingOrder ||
          lu.AssignmentType === AssignmentType.AssignmentPickup,
        dispFrom: lu.DispoDate,
      }))
      .filter((lu, index, self) => index === self.findIndex(t => t.city === lu.city));
  }

  onAppointmentRendered(e: AppointmentRenderedEvent): void {
    const appointmentData = e.appointmentData as Appointment;

    if (this.showCompactView) {
      e.appointmentElement.classList.add('compact-view');
    }

    if (
      appointmentData.AppointmentType === AppointmentType.LONG_WEEK ||
      appointmentData.AppointmentType === AppointmentType.SHORT_WEEK
    ) {
      e.appointmentElement.classList.add('week-appointment-height');
    }
  }

  onDragStart = (e: DragStartEvent) => {
    const appointment = e.itemData;

    if (appointment.DriverState !== DriverState.NotDispatched) {
      e.cancel = true;
      notify(this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.DRIVER_INFORMED'), 'error', 3000);
    }

    if (appointment.ProcessingState !== ProcessingState.TransportationScheduling) {
      e.cancel = true;
      notify(this.translationService.instant('PROJECT_PLANNING.NOTIFICATION.PROJECT_PLANNED'), 'error', 3000);
    }
  };

  private computeFormattedLoadunits(loadunits: SchedulerProjectLoadunitDto[] | undefined): FormattedLoadunit[] {
    if (!loadunits || loadunits.length === 0) {
      return [];
    }

    const seenCities = new Set<string>();
    const result: FormattedLoadunit[] = [];

    for (const lu of loadunits) {
      if (!lu.ActionCity) continue;
      if (seenCities.has(lu.ActionCity)) continue;

      seenCities.add(lu.ActionCity);

      result.push({
        city: lu.ActionCity,
        isPickup:
          lu.AssignmentType === AssignmentType.PickupRentalPurchasingOrder ||
          lu.AssignmentType === AssignmentType.AssignmentPickup,
        dispFrom: lu.DispoDate,
      });
    }

    return result;
  }

  private computeProjectColor(appointmentData: Appointment): string {
    const baseColor =
      this.processingStateColors[appointmentData.ProcessingState || ProcessingState.TransportationScheduling];

    return appointmentData.checkNeeded ? `linear-gradient(to right, ${baseColor} 90%, red 100%)` : baseColor;
  }

  private computeAppointmentColor(appointment: Appointment): string {
    switch (appointment.AppointmentType) {
      case AppointmentType.LONG_WEEK:
      case AppointmentType.SHORT_WEEK:
        return this.getWeekColor(appointment);
      case AppointmentType.PROJECT:
        return this.computeProjectColor(appointment);
      default:
        return '#FFFFFF';
    }
  }

  private computeLoadunitsContainerColor(appointmentData: Appointment): string | null {
    if (appointmentData.Loadunits?.some(loadunit => loadunit.ChangeState !== 0)) {
      return COLORS.SOFT_VIOLET;
    }

    return this.processingStateColors[appointmentData.ProcessingState || ProcessingState.TransportationScheduling];
  }

  private computeAdminVisible(appointmentData: Appointment): boolean {
    if (this.selectedAdministrators.length === 0) {
      return true;
    }

    return this.selectedAdministrators.includes(appointmentData.Administrator ?? '');
  }

  private toAppointmentVm(appointment: Appointment): AppointmentVm {
    const isWeekAppointment =
      appointment.AppointmentType === AppointmentType.LONG_WEEK ||
      appointment.AppointmentType === AppointmentType.SHORT_WEEK;

    return {
      ...appointment,
      formattedLoadunits: this.computeFormattedLoadunits(appointment.Loadunits as SchedulerProjectLoadunitDto[]),
      appointmentColor: this.computeAppointmentColor(appointment),
      loadunitsContainerColor: this.computeLoadunitsContainerColor(appointment),
      isWeekAppointment,
      isAdminVisible: this.computeAdminVisible(appointment),
      appointmentCssClass: this.getAppointmentClass(appointment),
      isPrimeLogistic: this.computeIsPrimeLogistic(appointment),
    };
  }

  private computeIsPrimeLogistic(appointment: Appointment): boolean {
    return (
      (appointment.Loadunits as SchedulerProjectLoadunitDto[] | undefined)?.some(lu => lu.IsPrimeLogistic) ?? false
    );
  }

  private updateAdminVisibilityOnly(): void {
    this.appointments = this.appointments.map(a => {
      const vm = a as AppointmentVm;
      return {
        ...vm,
        isAdminVisible: this.computeAdminVisible(vm),
      };
    });
  }

  private async computeCheckNeeded(appointment: Appointment): Promise<boolean> {
    if (!appointment.Loadunits || appointment.Loadunits.length === 0) {
      return false;
    }

    const appointmentKey = this.createAppointmentCheckKey(appointment);
    const cachedAppointmentResult = this.appointmentCheckCache.get(appointmentKey);
    if (cachedAppointmentResult !== undefined) {
      return cachedAppointmentResult;
    }

    const checks = await Promise.all(
      (appointment.Loadunits as SchedulerProjectLoadunitDto[]).map(loadunit =>
        this.computeLoadunitCheck(appointment, loadunit)
      )
    );

    const checkNeeded = checks.some(Boolean);
    this.appointmentCheckCache.set(appointmentKey, checkNeeded);
    return checkNeeded;
  }

  private async computeLoadunitCheck(
    appointment: Appointment,
    loadunit: SchedulerProjectLoadunitDto
  ): Promise<boolean> {
    const loadunitKey = this.createLoadunitCheckKey(appointment, loadunit);
    const cached = this.timelinessCache.get(loadunitKey);
    if (cached !== undefined) {
      return cached;
    }

    const result = await this.planningHelperService.verifyTimeliness(
      {
        FromDateTime: appointment.FromDateTime,
        UntilDateTime: appointment.UntilDateTime,
      },
      {
        AssignmentNo: loadunit.AssignmentNo,
        Type: loadunit.AssignmentType,
        EventDate: loadunit.EventDate ?? '',
      },
      new Date(loadunit.AssignmentDispoFrom),
      new Date(loadunit.AssignmentDispoTo)
    );

    const checkNeeded = result?.checkNeeded === true;
    this.timelinessCache.set(loadunitKey, checkNeeded);
    return checkNeeded;
  }

  private createAppointmentCheckKey(appointment: Appointment): string {
    const loadunits = (appointment.Loadunits as SchedulerProjectLoadunitDto[] | undefined) ?? [];
    const parts = loadunits.map(lu =>
      [
        lu.AssignmentNo ?? '',
        lu.AssignmentType ?? '',
        lu.EventDate ?? '',
        lu.AssignmentDispoFrom ?? '',
        lu.AssignmentDispoTo ?? '',
      ].join('|')
    );

    return [
      appointment.ProjectNo ?? '',
      appointment.FromDateTime ?? '',
      appointment.UntilDateTime ?? '',
      parts.join('||'),
    ].join('###');
  }

  private createLoadunitCheckKey(appointment: Appointment, loadunit: SchedulerProjectLoadunitDto): string {
    return [
      appointment.ProjectNo ?? '',
      appointment.FromDateTime ?? '',
      appointment.UntilDateTime ?? '',
      loadunit.AssignmentNo ?? '',
      loadunit.AssignmentType ?? '',
      loadunit.EventDate ?? '',
      loadunit.AssignmentDispoFrom ?? '',
      loadunit.AssignmentDispoTo ?? '',
    ].join('###');
  }

  private requestSchedulerRepaint(): void {
    if (this.repaintQueued) {
      return;
    }

    this.repaintQueued = true;

    setTimeout(() => {
      this.repaintQueued = false;
      this.repaintScheduler();
      this.cdr.markForCheck();
    }, 0);
  }

  private sameVehicleIds(current: Vehicle[], next: Vehicle[]): boolean {
    if (current.length !== next.length) {
      return false;
    }

    for (let i = 0; i < current.length; i += 1) {
      if (current[i].id !== next[i].id) {
        return false;
      }
    }

    return true;
  }

  private notifyError(err: unknown): void {
    if (err && typeof err === 'object' && 'error' in err) {
      const backendError = (err as { error?: string }).error;
      notify(this.translationService.instant('ERROR.' + backendError), 'error', 3000);
      return;
    }

    notify(this.translationService.instant('ERROR.UNKNOWN'), 'error', 3000);
  }

  private refreshSchedulerViewState(): void {
    if (!this.scheduler?.instance) {
      return;
    }

    const freshViews: SchedulerViews = this.views.map(view =>
      typeof view === 'string' ? view : ({ ...view } as SchedulerView)
    );

    this.scheduler.instance.beginUpdate();
    this.scheduler.instance.option('views', freshViews);
    this.scheduler.instance.option('currentView', this.viewName);
    this.scheduler.instance.option('currentDate', this.fromDate ? new Date(this.fromDate) : new Date());
    this.scheduler.instance.endUpdate();
  }

  private async recreateSchedulerAfterInitialLoad(): Promise<void> {
    this.schedulerReady = false;
    this.cdr.markForCheck();

    await new Promise(resolve => setTimeout(resolve, 0));

    this.schedulerReady = true;
    this.cdr.markForCheck();
  }
}
